import type { AppMount } from './apps-mounts';

/**
 * Where a tenant admin can open an app: on the site, and in its own admin.
 *
 * The app list used to offer one way in — the app's `base_url`, in a new tab.
 * That is the bare app origin with no frame around it, so no `rumio_session`:
 * the admin arrives as an anonymous visitor, can't reach the app's admin
 * area, and is told to "sign in" by an app that cannot sign anyone in from
 * there. An app is only itself inside its mount, where `AppEmbed` hands it a
 * session — so these links go to the mount.
 *
 * `admin_path` (contract v1.20) is the app's own admin route. The host loads
 * `/<prefix><path>` as the app at `<path>`, so the admin link is the mount
 * prefix joined to it. Validated on save (`validateAdminPath`); re-checked
 * here because the manifest arrives as opaque JSON and becomes an href.
 */
export interface AppEntryLinks {
  /** The app on the site, e.g. `/game`. */
  site: string;
  /** The app's own admin area on the site, e.g. `/game/admin`, when declared. */
  admin: string | null;
}

const ADMIN_PATH = /^\/(?:[A-Za-z0-9_-]+(?:\/[A-Za-z0-9_-]+)*)?$/;

/** The declared admin path, or null for anything that is not a plain app path. */
export function adminPathOf(manifest: Record<string, unknown> | null | undefined): string | null {
  const raw = manifest?.admin_path;
  return typeof raw === 'string' && raw.length <= 200 && ADMIN_PATH.test(raw) ? raw : null;
}

/** The parts of a mount these links need; both the public and admin shapes have them. */
export interface MountLike {
  path_prefix: string;
  sort_order: number;
  /** Admin rows carry it; the public list is active mounts only. */
  is_active?: boolean;
}

/**
 * Links from one app's mounts and manifest. The first active mount by
 * `sort_order` (the one the header lists first) wins. `null` when the app has
 * no active mount: then there is nowhere on the site to open it, and the page
 * says so rather than offering `base_url`.
 */
export function entryLinksFrom(
  mounts: readonly MountLike[],
  manifest: Record<string, unknown> | null | undefined,
): AppEntryLinks | null {
  const first = mounts.filter((m) => m.is_active !== false).sort((a, b) => a.sort_order - b.sort_order)[0];
  if (!first) return null;
  const site = `/${first.path_prefix.replace(/^\/+|\/+$/g, '')}`;
  const adminPath = adminPathOf(manifest);
  return {
    site,
    admin: adminPath === null ? null : adminPath === '/' ? site : `${site}${adminPath}`,
  };
}

/** Links for one app, from the tenant's public list of active mounts. */
export function appEntryLinks(mounts: readonly AppMount[] | undefined, appId: string): AppEntryLinks | null {
  const mine = (mounts ?? []).filter((m) => m.app.id === appId);
  return mine.length ? entryLinksFrom(mine, mine[0].app.manifest) : null;
}
